import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import workbenchLogo from '../assets/workbench_logo.png';

const formatCurrency = (amount) => {
    const safeAmount = Number(amount || 0);
    return `PKR ${safeAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (date) => {
    if (!date) return 'N/A';
    return moment(date).format('DD MMM YYYY');
};

const loadImageAsDataUrl = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve({ dataUrl: canvas.toDataURL('image/png'), width: img.width, height: img.height });
    };
    img.onerror = reject;
    img.src = src;
});

const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function convertLessThanOneThousand(num) {
    if (num === 0) return '';
    let str = '';
    if (num >= 100) {
        str += ones[Math.floor(num / 100)] + ' Hundred ';
        num %= 100;
    }
    if (num >= 20) {
        str += tens[Math.floor(num / 10)] + ' ';
        num %= 10;
    }
    if (num > 0) {
        str += ones[num] + ' ';
    }
    return str;
}

function numberToWords(num) {
    if (num === 0) return 'Zero';
    let str = '';
    const millions = Math.floor(num / 1000000);
    const thousands = Math.floor((num % 1000000) / 1000);
    const remainder = Math.floor(num % 1000);

    if (millions > 0) {
        str += convertLessThanOneThousand(millions) + 'Million ';
    }
    if (thousands > 0) {
        str += convertLessThanOneThousand(thousands) + 'Thousand ';
    }
    if (remainder > 0) {
        str += convertLessThanOneThousand(remainder);
    }
    return str.trim();
}

/**
 * Download a single invoice as PDF
 * @param {Object} invoice - populated invoice object
 * @param {Object} [settings] - optional invoice settings (notes, termsAndConditions)
 */
export const downloadInvoicePdf = async (invoice, settings = {}) => {
    if (!invoice) return false;

    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const left = 40;
    const right = 40;
    let y = 40;

    // Load Logo
    try {
        const { dataUrl, width, height } = await loadImageAsDataUrl(workbenchLogo);
        const targetHeight = 40;
        const ratio = width / height;
        const targetWidth = targetHeight * ratio;
        doc.addImage(dataUrl, 'PNG', left, y, targetWidth, targetHeight);
    } catch (err) {
        // continue without logo if error
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(22);
        doc.setTextColor(0, 0, 0);
        doc.text('WorkBench', left, y + 25);
    }

    // Header Right
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(30);
    doc.setTextColor(0, 0, 0);
    doc.text('Invoice', pageWidth - right, y + 25, { align: 'right' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`# ${invoice.invoiceNumber || ''}`, pageWidth - right, y + 45, { align: 'right' });

    // Company Address
    y += 70;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text('13-D Commercial Valancia Town Lahore.', left, y);
    doc.text('Lahore Punjab 54000', left, y + 14);
    doc.text('Pakistan', left, y + 28);

    // Balance Due Top Right
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text('Balance Due', pageWidth - right, y + 14, { align: 'right' });
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(formatCurrency(invoice.balanceAmount).replace('PKR ', 'PKR'), pageWidth - right, y + 28, { align: 'right' });

    y += 70;

    // Invoice Details Right
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);

    const detailsRightY = y;
    doc.text('Invoice Date :', pageWidth - right - 120, detailsRightY, { align: 'right' });
    doc.text(formatDate(invoice.createdDate), pageWidth - right, detailsRightY, { align: 'right' });

    doc.text('Terms :', pageWidth - right - 120, detailsRightY + 20, { align: 'right' });
    doc.text('Due On Receipt', pageWidth - right, detailsRightY + 20, { align: 'right' });

    doc.text('Due Date :', pageWidth - right - 120, detailsRightY + 40, { align: 'right' });
    doc.text(formatDate(invoice.dueDate), pageWidth - right, detailsRightY + 40, { align: 'right' });

    // Bill To
    doc.setTextColor(80, 80, 80);
    doc.text('Bill To', left, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    const customer = invoice.customer || {};
    doc.text(customer.name || 'Customer', left, y + 16);

    y += 40;

    // Subject
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('Subject :', left, y);
    doc.setTextColor(0, 0, 0);
    doc.text('Monthly Rental', left, y + 16);

    y += 40;

    // ── Step 1: Classify every line item ───────────────────────────────────────
    const SECURITY_TYPE = 'security_deposit';
    const ADVANCE_TYPE = 'advance_deduction';

    const rentItems = [];
    const securityItems = [];
    const advanceItems = [];

    for (const item of invoice.lineItems || []) {
        if (item.type === SECURITY_TYPE) {
            securityItems.push(item);
        } else if (item.type === ADVANCE_TYPE) {
            advanceItems.push(item);
        } else {
            rentItems.push(item);
        }
    }

    // ── Step 2: Build clean description for rent items (no numbers) ────────────
    const getCleanDescription = (item) => {
        const booking = item.booking;
        if (!booking) return item.description || 'Monthly Rent';

        // Office booking
        if (booking.office) return 'Office - Monthly Rent';

        // Seat booking: use seatType from nested populate
        if (booking.seat) {
            const seatType = booking.seat.seatType;
            if (seatType === 'dedicated') return 'Dedicated Seat - Monthly Rent';
            return 'Shared Seat - Monthly Rent'; // default to shared
        }

        return item.description || 'Monthly Rent';
    };

    // ── Step 3: Group rent items by description + unitPrice ───────────────────
    const grouped = [];
    for (const item of rentItems) {
        const desc = getCleanDescription(item);
        const price = Number(item.unitPrice || 0);
        const existing = grouped.find(g => g.description === desc && g.unitPrice === price);
        if (existing) {
            existing.quantity += (item.quantity || 1);
            existing.amount += Number(item.amount || 0);
        } else {
            grouped.push({
                description: desc,
                quantity: item.quantity || 1,
                unitPrice: price,
                amount: Number(item.amount || 0),
            });
        }
    }

    // ── Step 4: Compute totals ─────────────────────────────────────────────────
    const rentSubtotal = grouped.reduce((s, g) => s + g.amount, 0);
    const securityTotal = securityItems.reduce((s, i) => s + Number(i.amount || 0), 0);
    const advanceTotal = advanceItems.reduce((s, i) => s + Number(i.amount || 0), 0); // negative
    const grandTotal = rentSubtotal + securityTotal + advanceTotal;

    // ── Step 5: Build main table rows (rent only) ──────────────────────────────
    const lineItemRows = grouped.map((g, idx) => [
        String(idx + 1),
        g.description,
        Number(g.quantity).toFixed(2),
        Number(g.unitPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        Number(g.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    ]);

    autoTable(doc, {
        startY: y,
        margin: { left, right },
        head: [['#', 'Item & Description', 'Qty', 'Rate', 'Amount']],
        body: lineItemRows,
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 8, textColor: [50, 50, 50] },
        headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255], fontStyle: 'normal' },
        columnStyles: {
            0: { cellWidth: 30 },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 50, halign: 'right' },
            3: { cellWidth: 90, halign: 'right' },
            4: { cellWidth: 90, halign: 'right' },
        },
        willDrawCell: (data) => {
            // Light bottom border for body rows
            if (data.section === 'body') {
                doc.setDrawColor(220, 220, 220);
                doc.setLineWidth(0.5);
                doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
            }
        }
    });

    y = doc.lastAutoTable.finalY + 20;

    // ── Step 6: Totals section ────────────────────────────────────────────────
    const fmt = (n) => Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const totalsData = [
        ['Subtotal', fmt(rentSubtotal)],
    ];

    if (securityTotal !== 0) {
        totalsData.push(['Security Deposit', fmt(securityTotal)]);
    }

    if (advanceTotal !== 0) {
        totalsData.push(['Advance Deduction', `(-) ${fmt(Math.abs(advanceTotal))}`]);
    }

    totalsData.push(['Total', formatCurrency(grandTotal).replace('PKR ', 'PKR')]);

    const paidAmount = Number(invoice.paidAmount || 0);
    totalsData.push(['Payment Made', `(-) ${fmt(paidAmount)}`]);

    autoTable(doc, {
        startY: y,
        margin: { left: pageWidth - right - 220, right },
        body: totalsData,
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 6, textColor: [50, 50, 50] },
        columnStyles: {
            0: { halign: 'right', cellWidth: 120 },
            1: { halign: 'right', cellWidth: 100 },
        },
        willDrawCell: (data) => {
            const totalRowIndex = totalsData.findIndex(r => r[0] === 'Total');
            if (data.row.index === totalRowIndex && data.section === 'body') {
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(0, 0, 0);
            }
            const paidRowIndex = totalsData.findIndex(r => r[0] === 'Payment Made');
            if (data.row.index === paidRowIndex && data.section === 'body' && data.column.index === 1) {
                doc.setTextColor(220, 53, 69); // Red for Payment Made
            }
        }
    });

    y = doc.lastAutoTable.finalY;

    // Balance Due Box
    const balanceDue = Number(invoice.balanceAmount ?? (grandTotal - paidAmount));
    const balanceDueFormatted = formatCurrency(balanceDue).replace('PKR ', 'PKR');
    autoTable(doc, {
        startY: y,
        margin: { left: pageWidth - right - 220, right },
        body: [['Balance Due', balanceDueFormatted]],
        theme: 'plain',
        styles: { fontSize: 10, cellPadding: 8, fillColor: [240, 240, 240] },
        columnStyles: {
            0: { fontStyle: 'bold', halign: 'right', cellWidth: 120, textColor: [0, 0, 0] },
            1: { fontStyle: 'bold', halign: 'right', cellWidth: 100, textColor: [0, 0, 0] },
        },
    });

    y = doc.lastAutoTable.finalY + 20;

    // Total in Words
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    const words = numberToWords(Math.floor(invoice.totalAmount || 0));
    doc.text('Total In Words:', pageWidth - right - 220, y, { align: 'right' });
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(50, 50, 50);

    // Split text if it's too long
    const splitWords = doc.splitTextToSize(`${words} Only`, 200);
    doc.text(splitWords, pageWidth - right - 210, y, { align: 'left' });

    y += Math.max(40, splitWords.length * 14);

    // Notes
    const notesText = settings.notes ||
        `Thanks for your business.\n\nBank Details:\nSoneri Bank Ltd\nPK45SONE0034320009766147\nNOTE: Please share payment proof once the payment is done.`;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(80, 80, 80);
    doc.text('Notes', left, y);
    y += 16;
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    const splitNotes = doc.splitTextToSize(notesText, pageWidth - left - right);
    doc.text(splitNotes, left, y);
    y += (splitNotes.length * 12) + 20;

    // Terms & Conditions
    const termsText = settings.termsAndConditions ||
        `All Payments are non-refundable.\nSecurity deposit will only be adjusted against your last month rent.`;

    doc.setFontSize(11);
    doc.setTextColor(80, 80, 80);
    doc.text('Terms & Conditions', left, y);
    y += 16;
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    const splitTerms = doc.splitTextToSize(termsText, pageWidth - left - right);
    doc.text(splitTerms, left, y);

    // Footer
    const totalPages = doc.getNumberOfPages();
    for (let page = 1; page <= totalPages; page++) {
        doc.setPage(page);
        doc.setFontSize(8);
        doc.setTextColor(150);
        const pageHeight = doc.internal.pageSize.getHeight();

        doc.setDrawColor(200);
        doc.setLineWidth(0.5);
        doc.line(left, pageHeight - 30, pageWidth - right, pageHeight - 30);

        doc.text(`${page}`, pageWidth - right, pageHeight - 15, { align: 'right' });
    }

    const fileName = `${invoice.invoiceNumber || 'invoice'}.pdf`;
    doc.save(fileName);
    return true;
};
